import { logger, roundTo } from '@/lib/client'
import { TrackingProductsSchema } from '@/schemas/tracking.product'
import type { z } from 'zod'
import facebook from './analytics/facebook'

declare const dataLayer: unknown[]

export const AddToCart = async (
  properties: z.infer<typeof TrackingProductsSchema>,
): Promise<void> => {
  const event = 'Adicionar produto no carrinho'

  try {
    if (TrackingProductsSchema.parse(properties)) {
      facebook.trackAddToCart(properties)

      if (dataLayer) {
        dataLayer.push({ ecommerce: null })
        dataLayer.push({
          event: 'add_to_cart',
          ecommerce: {
            currency: 'BRL',
            value: properties.Valor,
            items: [
              {
                item_id: properties.ProdutoID,
                item_name: properties.Produto,
                concurso: properties.Instituicao,
                carreira: properties.Carreira,
                discount: properties.Desconto,
                index: 0,
                status: properties.Edital,
                item_category: properties.Tipo,
                cargo: properties.Cargo,
                price: properties.Valor,
                quantity: 1,
                formacao: properties.Formacao,
              },
            ],
          },
        })

        dataLayer.push({ items: null })
        dataLayer.push({
          event: 'rmkt_add_to_cart',
          value: properties.Valor,
          items: [
            {
              id: properties.ProdutoID,
              google_business_vertical: 'education',
            },
          ],
        })
      }

      if (window.posthog) {
        window.posthog.capture(event, {
          id: properties.ProdutoID,
          price: roundTo(properties.Valor, 2),
          name: properties.Produto,
          institution: properties.Instituicao,
          career: properties.Carreira,
          discount: roundTo(properties.Desconto, 2),
          status: properties.Edital,
          category: properties.Tipo,
          role: properties.Cargo,
          quantity: 1,
          scholarity: properties.Formacao,
        })
      }
    }
  } catch (error) {
    logger(
      'TRACKING ADD TO CART',
      JSON.stringify({
        event: event,
        name: error?.name,
        message: error?.message,
      }),
    )
  }
}

export const CartImpression = async (properties: {
  items: Array<z.infer<typeof TrackingProductsSchema>>
}): Promise<void> => {
  const event = 'Visualizar página do carrinho'

  try {
    const items = properties.items
      .map((p: z.infer<typeof TrackingProductsSchema>) => {
        if (TrackingProductsSchema.parse(p)) {
          return {
            item_id: p.ProdutoID,
            item_name: p.Produto,
            concurso: p.Instituicao,
            carreira: p.Carreira,
            cargo: p.Cargo,
            discount: p.Desconto,
            status: p.Edital,
            item_category: p.Tipo,
            price: p.Valor,
            quantity: 1,
            formacao: p.Formacao,
          }
        }
      })
      .filter((t) => t)

    if (dataLayer && items.length) {
      dataLayer.push({ ecommerce: null })
      dataLayer.push({
        event: 'view_cart',
        ecommerce: {
          currency: 'BRL',
          value: items.reduce((a, b) => a + (b?.price || 0), 0),
          items,
        },
      })
    }

    if (window.posthog) {
      const price = items.reduce((a, b) => a + (b?.price || 0), 0)
      const discount = items.reduce((a, b) => a + (b?.discount || 0), 0)
      window.posthog.capture(event, {
        price: roundTo(price, 2),
        discount: roundTo(discount, 2),
        avg: roundTo(price / items.length, 2),
        items: items.length,
      })
    }
  } catch (error) {
    logger(
      'TRACKING CART IMPRESSION',
      JSON.stringify({
        event: event,
        name: error?.name,
        message: error?.message,
      }),
    )
  }
}

export const RemoveFromCart = async (
  properties: z.infer<typeof TrackingProductsSchema>,
): Promise<void> => {
  const event = 'Remover produto no carrinho'

  try {
    if (TrackingProductsSchema.parse(properties)) {
      if (dataLayer) {
        dataLayer.push({ ecommerce: null })
        dataLayer.push({
          event: 'remove_from_cart',
          ecommerce: {
            currency: 'BRL',
            value: properties.Valor,
            items: [
              {
                item_id: properties.ProdutoID,
                item_name: properties.Produto,
                concurso: properties.Instituicao,
                carreira: properties.Carreira,
                discount: properties.Desconto,
                index: 0,
                status: properties.Edital,
                item_category: properties.Tipo,
                cargo: properties.Cargo,
                price: properties.Valor,
                quantity: 1,
                formacao: properties.Formacao,
              },
            ],
          },
        })
      }

      if (window.posthog) {
        window.posthog.capture(event, {
          id: properties.ProdutoID,
          price: roundTo(properties.Valor, 2),
          name: properties.Produto,
          institution: properties.Instituicao,
          career: properties.Carreira,
          discount: roundTo(properties.Desconto, 2),
          status: properties.Edital,
          category: properties.Tipo,
          role: properties.Cargo,
          quantity: 1,
          scholarity: properties.Formacao,
        })
      }
    }
  } catch (error) {
    logger(
      'TRACKING REMOVE FROM CART',
      JSON.stringify({
        event: event,
        name: error?.name,
        message: error?.message,
      }),
    )
  }
}
